"use client";
import React from "react";
import { FormattedMessage, FormattedNumber, useIntl } from "react-intl";
import { Alert } from "@akashnetwork/ui/components";
import { usePopup } from "@akashnetwork/ui/context";
import { ArrowRight } from "iconoir-react";

import { useChainParam } from "@src/hooks/useChainParam/useChainParam";
import { useWalletBalance } from "@src/hooks/useWalletBalance";

export const useManagedEscrowFaqModal = (): {
  showManagedEscrowFaqModal: () => void;
} => {
  const intl = useIntl();
  const { minDeposit } = useChainParam();
  const { balance: walletBalance } = useWalletBalance();
  const { createCustom } = usePopup();

  const showManagedEscrowFaqModal = () => {
    createCustom({
      actions: [],
      fullWidth: true,
      title: intl.formatMessage({ id: "hooks.managedescrowfaq.title", defaultMessage: "FAQ - Deployments" }),
      maxWidth: "sm",
      enableCloseOnBackdropClick: true,
      message: (
        <>
          {walletBalance && (
            <div className="mb-4 flex items-center space-x-2">
              <div className="flex items-center justify-between space-x-2">
                <span className="text-xs text-muted-foreground">
                  <FormattedMessage id="hooks.managedescrowfaq.available" defaultMessage="Available:" />
                </span>
                <span className="font-bold">
                  <FormattedNumber
                    value={walletBalance.totalDeploymentGrantsUSD}
                    // eslint-disable-next-line react/style-prop-object
                    style="currency"
                    currency="USD"
                  />
                </span>
              </div>

              <div className="flex items-center justify-between space-x-2">
                <span className="text-xs text-muted-foreground">
                  <FormattedMessage id="hooks.managedescrowfaq.deposits" defaultMessage="Deposits:" />
                </span>
                <span>
                  <FormattedNumber
                    value={walletBalance.totalDeploymentEscrowUSD}
                    // eslint-disable-next-line react/style-prop-object
                    style="currency"
                    currency="USD"
                  />
                </span>
              </div>
            </div>
          )}
          <div className="space-y-2">
            <Alert className="space-y-2">
              <h2 className="font-bold">
                <FormattedMessage id="hooks.managedescrowfaq.howtitle" defaultMessage="How do Junoa deployments work?" />
              </h2>
              <p className="text-sm text-muted-foreground">
                <FormattedMessage
                  id="hooks.managedescrowfaq.howbody"
                  defaultMessage="Junoa deployments use escrow accounts, also known as deployment deposits, as a way to ensure that a user has enough funds to cover the cost of deploying and running their application on the Junoa Network. When you create a deployment, you deposit {amount} into the account."
                  values={{
                    amount: (
                      <FormattedNumber
                        value={minDeposit.act}
                        // eslint-disable-next-line react/style-prop-object
                        style="currency"
                        currency="USD"
                      />
                    )
                  }}
                />
              </p>

              <div>
                <span className="text-xs italic">
                  <FormattedMessage id="hooks.managedescrowfaq.createdeployment" defaultMessage="Create deployment" />
                </span>
                <div className="flex items-center space-x-2 text-sm">
                  <span className="text-success">
                    <FormattedMessage id="hooks.managedescrowfaq.availableshort" defaultMessage="Available" />
                  </span>
                  <span>{minDeposit.act}$</span>
                  <ArrowRight className="text-xs" />
                  <span>
                    <FormattedMessage id="hooks.managedescrowfaq.deposit" defaultMessage="Deposit" />
                  </span>
                </div>
              </div>

              <p className="text-sm text-muted-foreground">
                <FormattedMessage
                  id="hooks.managedescrowfaq.collateralbody"
                  defaultMessage="This deposit acts as collateral and is used to pay for the resources consumed by your application. Junoa charges by the minute and once you close your deployment, the remaining balance is transferred back to your available balance."
                />
              </p>

              <div>
                <span className="text-xs italic">
                  <FormattedMessage id="hooks.managedescrowfaq.closedeployment" defaultMessage="Close deployment" />
                </span>
                <div className="flex items-center space-x-2 text-sm">
                  <span>
                    <FormattedMessage id="hooks.managedescrowfaq.deposit" defaultMessage="Deposit" />
                  </span>
                  <span>{minDeposit.act}$</span>
                  <ArrowRight className="text-xs" />
                  <span className="text-success">
                    <FormattedMessage id="hooks.managedescrowfaq.availableshort" defaultMessage="Available" />
                  </span>
                </div>
              </div>
            </Alert>

            <Alert className="space-y-2">
              <h2 className="font-bold">
                <FormattedMessage id="hooks.managedescrowfaq.providerstitle" defaultMessage="About Providers" />
              </h2>
              <p className="text-sm text-muted-foreground">
                <FormattedMessage
                  id="hooks.managedescrowfaq.providersbody"
                  defaultMessage="Each provider has a unique configuration when it comes to escrow accounts. Withdrawals from the escrow account to the provider can be any value starting at 5 minutes. The cost stays the same, but the escrow balance of your deployments will be reduced by the amount withdrawn based on the provider configuration."
                />
              </p>
            </Alert>
          </div>
        </>
      )
    });
  };
  return {
    showManagedEscrowFaqModal
  };
};
